import React, { useState } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "@heroui/react";
import { useNavigate } from "react-router-dom";
import { usePlans } from "@/hooks/usePlans";
import { canAccessFeature } from "@/app/store/plans/types";
import { FEATURE_NAMES } from "@/app/store/plans/constants";
import { FaLock } from "react-icons/fa6";

interface PlanGuardProps {
  children: React.ReactNode;
  permission: number;
  fallback?: React.ReactNode;
  showModal?: boolean;
}

export const PlanGuard: React.FC<PlanGuardProps> = ({
  children,
  permission,
  fallback = null,
  showModal = true,
}) => {
  const { currentPlan } = usePlans();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const hasAccess = canAccessFeature(currentPlan, permission);
  const featureName =
    FEATURE_NAMES[permission as keyof typeof FEATURE_NAMES] || "этой функции";

  const handleClick = (e: React.MouseEvent) => {
    if (!hasAccess) {
      e.preventDefault();
      e.stopPropagation();
      if (showModal) {
        setIsModalOpen(true);
      }
    }
  };

  const handleViewPlans = () => {
    setIsModalOpen(false);
    navigate("/dashboard/plans");
  };

  if (hasAccess) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  return (
    <>
      <div className="relative cursor-not-allowed" onClick={handleClick}>
        <div className="blur-[1px] pointer-events-none">{children}</div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="p-2 bg-black/50 rounded-lg">
            <FaLock className="text-white text-lg drop-shadow-lg" />
          </div>
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            Функция недоступна
          </ModalHeader>
          <ModalBody>
            <p>
              У вашего текущего плана{" "}
              <strong>{currentPlan?.display_name || "Базовый"}</strong> нет
              доступа к {featureName}.
            </p>
            <p className="text-sm text-foreground-500 mt-2">
              Для использования этой функции необходимо перейти на более высокий
              тарифный план.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              color="default"
              variant="bordered"
              onPress={() => setIsModalOpen(false)}
            >
              Понятно
            </Button>
            <Button color="primary" onPress={handleViewPlans}>
              Просмотр планов
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
